import { memo } from 'react'

// Shimmer overlay component with GPU acceleration
const ShimmerOverlay = ({ delay = 0, opacity = 50 }) => (
  <div
    className="absolute inset-0 -translate-x-full animate-shimmer bg-gradient-to-r from-transparent via-white to-transparent"
    style={{
      willChange: "transform",
      backfaceVisibility: "hidden",
      animationDelay: `${delay}ms`,
      ["--shimmer-opacity"]: opacity / 100,
    }}
  />
);

export const Skeleton = ({ className, children }) => {
  return (
    <div className={`relative overflow-hidden bg-surface-active ${className}`}>
      <ShimmerOverlay />
      {children}
    </div>
  )
};

// Project Card Skeleton - matches actual ProjectCard layout
export const ProjectCardSkeleton = memo(function ProjectCardSkeleton() {
  return (
    <div className="border rounded-2xl bg-white overflow-hidden">
      {/* Gradient header area */}
      <div className="h-32 bg-surface-active relative overflow-hidden">
        <ShimmerOverlay opacity={50} />
        {/* Title placeholder lines */}
        <div className="absolute bottom-14 left-4 right-4 space-y-2">
          <div className="h-5 bg-surface-disabled rounded w-3/4 relative overflow-hidden">
            <ShimmerOverlay opacity={60} />
          </div>
        </div>
        {/* Owner attribution placeholder */}
        <div className="absolute bottom-3 right-3 flex items-center gap-2">
          <div className="h-3 bg-surface-disabled rounded w-20 relative overflow-hidden">
            <ShimmerOverlay opacity={60} />
          </div>
        </div>
      </div>

      {/* Progress bar area */}
      <div className="relative w-full h-1.5 bg-surface-hover">
        <div className="absolute inset-y-0 left-0 w-2/3 bg-surface-disabled relative overflow-hidden">
          <ShimmerOverlay opacity={50} />
        </div>
      </div>

      {/* Footer with avatars and link */}
      <div className="p-4 pt-8 border-t border-surface-border">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {/* Stacked avatar placeholders */}
            <div className="flex -space-x-2">
              {[1, 2, 3, 4].map((i) => (
                <div
                  key={i}
                  className="w-8 h-8 rounded-full bg-surface-active border-2 border-white relative overflow-hidden"
                >
                  <ShimmerOverlay delay={i * 100} opacity={60} />
                </div>
              ))}
            </div>
            {/* "+X more" placeholder */}
            <div className="h-3 bg-surface-active rounded w-14 relative overflow-hidden">
              <ShimmerOverlay opacity={60} />
            </div>
          </div>
          {/* Link placeholder */}
          <div className="h-4 bg-surface-active rounded w-28 relative overflow-hidden">
            <ShimmerOverlay opacity={60} />
          </div>
        </div>
      </div>
    </div>
  );
});

// Partner Card Skeleton - matches actual PartnerCard layout
export const PartnerCardSkeleton = memo(function PartnerCardSkeleton() {
  return (
    <div className="border rounded-3xl bg-white p-3 flex items-center gap-3 relative overflow-hidden">
      {/* Shimmer overlay */}
      <ShimmerOverlay opacity={40} />

      {/* Avatar placeholder */}
      <div className="w-16 h-16 rounded-full bg-surface-active flex-shrink-0 relative overflow-hidden">
        <ShimmerOverlay opacity={60} />
      </div>

      {/* Text lines */}
      <div className="flex-1 min-w-0 space-y-2">
        {/* Name */}
        <div className="h-4 bg-surface-disabled rounded w-3/4 relative overflow-hidden">
          <ShimmerOverlay opacity={60} />
        </div>
        {/* Designation */}
        <div className="h-3 bg-surface-active rounded w-1/2 relative overflow-hidden">
          <ShimmerOverlay opacity={60} />
        </div>
        {/* Address */}
        <div className="h-3 bg-surface-active rounded w-2/3 relative overflow-hidden">
          <ShimmerOverlay opacity={60} />
        </div>
      </div>

      {/* Star icon placeholder */}
      <div className="absolute top-3 right-3 w-5 h-5 bg-surface-active rounded relative overflow-hidden">
        <ShimmerOverlay opacity={60} />
      </div>
    </div>
  );
});

// Skeleton grid helpers
export const ProjectCardSkeletonGrid = memo(function ProjectCardSkeletonGrid({ count = 9 }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 base:grid-cols-3 gap-6">
      {Array.from({ length: count }, (_, index) => (
        <ProjectCardSkeleton key={index} />
      ))}
    </div>
  );
});

export const PartnerCardSkeletonGrid = memo(function PartnerCardSkeletonGrid({ count = 9 }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 base:grid-cols-3 gap-4 pb-6 mt-6">
      {Array.from({ length: count }, (_, index) => (
        <PartnerCardSkeleton key={index} />
      ))}
    </div>
  );
});
